/**
 * REAL-ASSET MAP PROPS (2026-11, owner mission "map assets more realistic").
 *
 * The scatter props in decor.ts were all procedural (a roughened icosahedron
 * for a boulder, three flat-shaded lobes for a bush). This swaps them for
 * real-photo assets, CC0 from Poly Haven (public/models/props/CREDITS.md):
 *  - ROCKS: photoscans decimated for a game budget (~5k triangles, 1k WebP
 *    colour + normal + roughness, meshopt).
 *  - BUSHES: built here from crossed alpha-tested cards wearing a LEAF-CLUSTER
 *    texture composed offline from photographed leaves (the standard game
 *    foliage technique — a scanned shrub is either a few-cm ground plant or
 *    hundreds of thousands of triangles).
 *
 * Same contract as the photo surfaces (pbrTextures.ts):
 *  - the fetch starts when a GameRenderer is built (never on the menus);
 *  - decor.ts ALWAYS builds the procedural prop and tags its InstancedMesh
 *    (`userData.propKind` + `propLift`), so a slow or failed load just keeps
 *    the old look;
 *  - GameRenderer.precompile() gives the load a bounded wait under the match
 *    load screen and calls upgradeMapProps() BEFORE its scene-wide shader
 *    compile, so the swap is never a mid-match pop-in or a cold compile.
 *
 * Each variant is one InstancedMesh (one draw call for the whole map); the
 * instance transforms are the procedural prop's own, split across variants
 * and re-seated on the ground (see SEAT).
 */
import * as THREE from "three";
import { loadGltf } from "./gltfAssets.js";
import { applyWind, uprightSway, windGeometry } from "./wind.js";
import { PINE_BOLE, PINE_LEAN_X, PINE_LEAN_Z, PLANT_BASE, PLANT_TOP, TREE_CROWN_BLOBS } from "./decor.js";

const BASE = "/models/props";
const LEAF_CLUSTER_URL = "/textures/foliage/leaf_cluster.webp";
const FIR_BRANCH_URL = "/textures/foliage/fir_branch.webp";

type Family = "rock" | "bush" | "treeCrown" | "pineCrown";
export type PropKind = Family | "darkRock";
/** which family's variants replace a tagged kind */
const FAMILY_OF: Record<PropKind, Family> = { rock: "rock", darkRock: "rock", bush: "bush", treeCrown: "treeCrown", pineCrown: "pineCrown" };

const ROCK_SCANS = ["rock_07", "rock_09", "namaqualand_boulder_05"];

/** How a family's variants sit on the ground relative to the procedural prop
 *  they replace. `scale`: size vs the procedural footprint (the procedural
 *  boulders were squashed to 65% height and sunk 40%, which shrank a scan with
 *  real proportions to a pebble). `embed`: fraction of the variant's height
 *  pushed below the ground so the base never shows a gap. */
const SEAT: Record<Family, { scale: number; embed: number }> = {
  rock: { scale: 1.5, embed: 0.18 },
  bush: { scale: 1.0, embed: 0.06 },
  // the crown replaces ONE PART of a two-part tree: it must keep the trunk's
  // exact transform, so no rescale and no re-seat
  treeCrown: { scale: 1.0, embed: 0 },
  pineCrown: { scale: 1.0, embed: 0 },
};

/** Tint for the neon-theme rocks: multiplies the scanned albedo down into the
 *  dusk palette the procedural dark rock used (#2b3145). */
const DARK_ROCK_TINT = new THREE.Color("#6a7090");
/** Desaturating multiply for the leaf texture (see the bush material). */
const FOLIAGE_TINT = new THREE.Color("#a3b393");

interface Variant { geo: THREE.BufferGeometry; mat: THREE.MeshStandardMaterial }
const variants: Record<Family, Variant[]> = { rock: [], bush: [], treeCrown: [], pineCrown: [] };
let loadAll: Promise<boolean> | null = null;

/** Bake a loaded scan into ONE geometry: centred on x/z, BASE at y=0,
 *  horizontal half-extent 1 (the procedural props' unit radius). */
function normalise(scene: THREE.Object3D): Variant | null {
  let mesh: THREE.Mesh | null = null;
  scene.updateMatrixWorld(true);
  scene.traverse((o) => { if (!mesh && (o as THREE.Mesh).isMesh) mesh = o as THREE.Mesh; });
  if (!mesh) return null;
  const m = mesh as THREE.Mesh;
  const geo = m.geometry.clone();
  geo.applyMatrix4(m.matrixWorld);
  // scans carry baked vertex colour on some assets; it fights the albedo map
  geo.deleteAttribute("color");
  geo.computeBoundingBox();
  const bb = geo.boundingBox!;
  const c = bb.getCenter(new THREE.Vector3());
  const half = Math.max(bb.max.x - bb.min.x, bb.max.z - bb.min.z) / 2;
  if (!(half > 0)) return null;
  geo.translate(-c.x, -bb.min.y, -c.z);
  geo.scale(1 / half, 1 / half, 1 / half);
  geo.computeBoundingBox();
  geo.computeBoundingSphere();
  geo.userData.shared = true;
  const mat = (m.material as THREE.MeshStandardMaterial).clone();
  mat.vertexColors = false;
  mat.userData.shared = true;
  return { geo, mat };
}

/** A CARD BUSH: `cards` quads scattered through a squat ellipsoid, each
 *  facing a random outward-ish direction, wearing the leaf-cluster texture.
 *  Normals are bent toward "away from the bush centre" so the lighting wraps
 *  round the whole clump like a volume (flat card normals light every card
 *  differently and read as a pile of paper). Unit radius, base at y=0. */
function cardBushGeometry(seed: number, cards: number): THREE.BufferGeometry {
  let s = seed * 9301 + 49297;
  const rnd = (): number => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };
  const H = 1.25;                       // bush height (unit radius 1)
  const cy = H * 0.5;
  const pos: number[] = [], nor: number[] = [], uv: number[] = [], idx: number[] = [];
  const centre = new THREE.Vector3(0, cy * 0.8, 0);
  const n = new THREE.Vector3(), t = new THREE.Vector3(), b = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0);
  const p = new THREE.Vector3(), q = new THREE.Vector3();
  for (let k = 0; k < cards; k++) {
    // card centre inside the ellipsoid, biased outward (the core is hidden)
    const a = rnd() * Math.PI * 2, el = (rnd() - 0.3) * 1.1, rr = 0.25 + 0.4 * Math.sqrt(rnd());
    const cx = Math.cos(a) * Math.cos(el) * rr, cz = Math.sin(a) * Math.cos(el) * rr;
    const cyy = cy + Math.sin(el) * rr * H * 0.6;
    // facing: mostly outward from the centre, randomised
    n.set(cx, (cyy - cy) * 0.6, cz).normalize()
      .add(new THREE.Vector3(rnd() - 0.5, rnd() - 0.5, rnd() - 0.5).multiplyScalar(1.1)).normalize();
    t.crossVectors(Math.abs(n.y) > 0.95 ? new THREE.Vector3(1, 0, 0) : up, n).normalize();
    b.crossVectors(n, t).normalize();
    const spin = rnd() * Math.PI * 2, cs = Math.cos(spin), sn = Math.sin(spin);
    const half = 0.55 + rnd() * 0.25;
    const base = pos.length / 3;
    for (const [u, v] of [[0, 0], [1, 0], [1, 1], [0, 1]] as const) {
      const du = (u - 0.5) * 2 * half, dv = (v - 0.5) * 2 * half;
      const x = du * cs - dv * sn, y = du * sn + dv * cs;
      p.set(cx, cyy, cz).addScaledVector(t, x).addScaledVector(b, y);
      p.y = Math.max(0, p.y);
      pos.push(p.x, p.y, p.z);
      // bent normal: 75% "away from the bush centre", 25% the card's own
      q.copy(p).sub(centre).normalize().multiplyScalar(0.75).addScaledVector(n, 0.25).normalize();
      nor.push(q.x, q.y, q.z);
      uv.push(u, v);
    }
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("normal", new THREE.Float32BufferAttribute(nor, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(idx);
  // same sway curve as the procedural bush (rooted base, swaying crown)
  windGeometry(geo, (_x, y) => uprightSway(y, 0.15, H));
  geo.computeBoundingBox();
  geo.computeBoundingSphere();
  geo.userData.shared = true;
  return geo;
}

/** A broadleaf CROWN of leaf-cluster cards in the procedural tree's own frame
 *  (it replaces the blob canopy, the bent trunk + branches stay). Cards
 *  cluster where decor.ts's crown blobs are, so the silhouette and footprint —
 *  and the branch ends that reach into the blobs — are unchanged. Normals bend
 *  round the WHOLE crown (light wraps it as one mass); vertex colour darkens
 *  the cards deep inside the crown (self-shadowing, the depth cue). */
function cardCrownGeometry(seed: number): THREE.BufferGeometry {
  let s = seed * 7919 + 104729;
  const rnd = (): number => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };
  const blobs = TREE_CROWN_BLOBS;
  // crown centre + reach, for the bent normals and the depth shading
  const cc = new THREE.Vector3();
  for (const b of blobs) cc.add(new THREE.Vector3(b[0], b[1], b[2]));
  cc.multiplyScalar(1 / blobs.length);
  let reach = 0;
  for (const b of blobs) reach = Math.max(reach, Math.hypot(b[0] - cc.x, b[1] - cc.y, b[2] - cc.z) + b[3]);
  const pos: number[] = [], nor: number[] = [], uv: number[] = [], col: number[] = [], idx: number[] = [];
  const n = new THREE.Vector3(), t = new THREE.Vector3(), bt = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0);
  const p = new THREE.Vector3(), q = new THREE.Vector3(), jit = new THREE.Vector3();
  for (const [bx, by, bz, r] of blobs) {
    const cards = Math.max(3, Math.round(r * 9));
    for (let k = 0; k < cards; k++) {
      // inside the blob, biased to its surface
      jit.set(rnd() - 0.5, rnd() - 0.5, rnd() - 0.5).normalize().multiplyScalar(r * (0.35 + 0.55 * Math.sqrt(rnd())));
      const cx = bx + jit.x, cy = by + jit.y * 0.85, cz = bz + jit.z;
      n.set(cx - cc.x, (cy - cc.y) * 0.7, cz - cc.z).normalize()
        .add(q.set(rnd() - 0.5, rnd() - 0.5, rnd() - 0.5).multiplyScalar(1.2)).normalize();
      t.crossVectors(Math.abs(n.y) > 0.95 ? q.set(1, 0, 0) : up, n).normalize();
      bt.crossVectors(n, t).normalize();
      const spin = rnd() * Math.PI * 2, cs = Math.cos(spin), sn = Math.sin(spin);
      const half = r * (0.75 + rnd() * 0.35);
      const base = pos.length / 3;
      for (const [u, v] of [[0, 0], [1, 0], [1, 1], [0, 1]] as const) {
        const du = (u - 0.5) * 2 * half, dv = (v - 0.5) * 2 * half;
        p.set(cx, cy, cz).addScaledVector(t, du * cs - dv * sn).addScaledVector(bt, du * sn + dv * cs);
        pos.push(p.x, p.y, p.z);
        q.copy(p).sub(cc).normalize().multiplyScalar(0.8).addScaledVector(n, 0.2).normalize();
        nor.push(q.x, q.y, q.z);
        uv.push(u, v);
        // depth: 0.55 at the crown's heart → 1.0 at its rim
        const f = Math.min(1, p.distanceTo(cc) / reach);
        const shade = 0.55 + 0.45 * f;
        col.push(shade * 0.92, shade, shade * 0.9);
      }
      idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("normal", new THREE.Float32BufferAttribute(nor, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  geo.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  geo.setIndex(idx);
  // SAME sway curve as the trunk (decor.ts plantSway) or the crown slides off it
  windGeometry(geo, (_x, y) => uprightSway(y, PLANT_BASE, PLANT_TOP));
  geo.computeBoundingBox();
  geo.computeBoundingSphere();
  geo.userData.shared = true;
  return geo;
}

/** A CONIFER crown of fir-branch cards in the procedural pine's frame (the
 *  bent bole stays). Whorls every ~20 cm up the trunk, 9-12 branches each,
 *  shrinking toward the tip — the classic spire. Each branch card runs from
 *  the trunk outward, DROOPS, and is ROLLED round its own axis by a random
 *  angle: real fir branches are flat sprays, and flat horizontal cards seen
 *  from eye level vanish edge-on, so the roll is what gives the tree volume
 *  from every side. A crossed pair of cards forms the leader at the top. */
function firCrownGeometry(seed: number): THREE.BufferGeometry {
  let s = seed * 6271 + 2741;
  const rnd = (): number => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };
  const pos: number[] = [], nor: number[] = [], uv: number[] = [], col: number[] = [], idx: number[] = [];
  const lean = (y: number): [number, number] => {
    const f = Math.max(0, y / PINE_BOLE) ** 1.5;
    return [PINE_LEAN_X * f, PINE_LEAN_Z * f];
  };
  const quad = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, d: THREE.Vector3,
    n: THREE.Vector3, shade: number): void => {
    const base = pos.length / 3;
    for (const [v, u, w] of [[a, 0, 0], [b, 1, 0], [c, 1, 1], [d, 0, 1]] as const) {
      pos.push(v.x, v.y, v.z); nor.push(n.x, n.y, n.z); uv.push(u, w);
      col.push(shade * 0.95, shade, shade * 0.95);
    }
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  };
  const Y0 = 0.85, TOP = 4.75;
  const dir = new THREE.Vector3(), side = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0);
  const n = new THREE.Vector3(), root = new THREE.Vector3();
  const A = new THREE.Vector3(), B = new THREE.Vector3(), C = new THREE.Vector3(), D = new THREE.Vector3();
  for (let y = Y0; y < TOP - 0.45; y += 0.2) {
    const f = (y - Y0) / (TOP - Y0);              // 0 bottom → 1 top
    const len = 1.15 * (1 - f) ** 0.9 + 0.18;     // branch reach
    const count = 9 + Math.floor(rnd() * 4);
    const off = rnd() * Math.PI * 2;
    for (let k = 0; k < count; k++) {
      const az = off + (k / count) * Math.PI * 2 + (rnd() - 0.5) * 0.5;
      const droop = 0.18 + rnd() * 0.28 + (1 - f) * 0.15; // lower boughs droop more
      dir.set(Math.cos(az) * Math.cos(droop), -Math.sin(droop), Math.sin(az) * Math.cos(droop));
      // card plane: rolled round the branch axis
      side.crossVectors(up, dir).normalize();
      const roll = (rnd() - 0.5) * 1.3;
      side.applyAxisAngle(dir, roll);
      const w = len * (0.68 + rnd() * 0.14);
      const [lx, lz] = lean(y);
      root.set(lx, y, lz).addScaledVector(dir, 0.04);
      A.copy(root).addScaledVector(side, -w / 2);
      B.copy(root).addScaledVector(dir, len).addScaledVector(side, -w / 2);
      C.copy(root).addScaledVector(dir, len).addScaledVector(side, w / 2);
      D.copy(root).addScaledVector(side, w / 2);
      // bent normal: outward + up (sky-lit tops, darker undersides read via
      // the vertex shade)
      n.set(Math.cos(az), 0.9, Math.sin(az)).normalize();
      quad(A, B, C, D, n, 0.6 + 0.4 * f + (rnd() - 0.5) * 0.1);
    }
  }
  // leader: two crossed vertical cards at the tip
  for (const az of [0, Math.PI / 2]) {
    const [lx, lz] = lean(TOP);
    side.set(Math.cos(az), 0, Math.sin(az)).multiplyScalar(0.16);
    A.set(lx, TOP - 0.7, lz).sub(side); B.set(lx, TOP + 0.15, lz).sub(side);
    C.set(lx, TOP + 0.15, lz).add(side); D.set(lx, TOP - 0.7, lz).add(side);
    n.set(Math.cos(az + Math.PI / 2), 0.6, Math.sin(az + Math.PI / 2)).normalize();
    quad(A, B, C, D, n, 1);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("normal", new THREE.Float32BufferAttribute(nor, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  geo.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  geo.setIndex(idx);
  windGeometry(geo, (_x, y) => uprightSway(y, PLANT_BASE, PLANT_TOP)); // = the pine trunk's curve
  geo.computeBoundingBox();
  geo.computeBoundingSphere();
  geo.userData.shared = true;
  return geo;
}

async function loadPines(maxAniso: number): Promise<boolean> {
  try {
    const tex = await new THREE.TextureLoader().loadAsync(FIR_BRANCH_URL);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = maxAniso;
    tex.userData.shared = true;
    // the needle wind of the procedural pine (decor.ts: 0.11 / 1.05)
    const mat = applyWind(new THREE.MeshStandardMaterial({
      map: tex, alphaTest: 0.4, side: THREE.DoubleSide, roughness: 0.9, metalness: 0, vertexColors: true,
    }), 0.11, 1.05);
    mat.userData.shared = true;
    for (let v = 0; v < 2; v++) variants.pineCrown.push({ geo: firCrownGeometry(v + 1), mat });
    return true;
  } catch (e) {
    console.warn("[props] fir-branch texture failed — procedural pines stay", e);
    return false;
  }
}

async function loadBushes(maxAniso: number): Promise<boolean> {
  try {
    const tex = await new THREE.TextureLoader().loadAsync(LEAF_CLUSTER_URL);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = maxAniso;
    tex.userData.shared = true;
    const mat = applyWind(new THREE.MeshStandardMaterial({
      // colour multiplies the photo leaves down: under the full day sun the
      // raw texture read as saturated cartoon green next to the photo ground
      map: tex, color: FOLIAGE_TINT, alphaTest: 0.42, side: THREE.DoubleSide, roughness: 0.85, metalness: 0,
    }), 0.05, 1.35);
    mat.userData.shared = true;
    for (let v = 0; v < 3; v++) variants.bush.push({ geo: cardBushGeometry(v + 1, 14 + v * 2), mat });
    // tree crowns: same leaves, vertex-shaded for depth, and the TRUNK's wind
    // (amp 0.17, freq 0.9 — decor.ts) so crown and bole sway as one
    const crownMat = applyWind(new THREE.MeshStandardMaterial({
      map: tex, color: FOLIAGE_TINT, alphaTest: 0.42, side: THREE.DoubleSide, roughness: 0.85, metalness: 0, vertexColors: true,
    }), 0.17, 0.9);
    crownMat.userData.shared = true;
    for (let v = 0; v < 2; v++) variants.treeCrown.push({ geo: cardCrownGeometry(v + 1), mat: crownMat });
    return true;
  } catch (e) {
    console.warn("[props] leaf-cluster texture failed — procedural bushes stay", e);
    return false;
  }
}

/** Kick off every prop download (idempotent). */
export function startMapPropLoad(enabled: boolean, maxAniso = 4): void {
  if (!enabled || loadAll) return;
  const rocks = Promise.all(ROCK_SCANS.map(async (n) => {
    const g = await loadGltf(`${BASE}/${n}.glb`);
    const v = g ? normalise(g.scene) : null;
    if (v) variants.rock.push(v);
    return !!v;
  })).then((ok) => ok.every(Boolean));
  loadAll = Promise.all([rocks, loadBushes(maxAniso), loadPines(maxAniso)]).then((ok) => ok.every(Boolean));
}

/** The raw load promise, no deadline (offline screenshot harness only). */
export function mapPropsLoadPromise(): Promise<boolean> | null { return loadAll; }

/** Bounded wait for the downloads (precompile, under the load screen).
 *  True when every family is in; a partial load still upgrades what landed. */
export async function mapPropsReady(maxMs: number): Promise<boolean> {
  if (!loadAll) return false;
  return Promise.race([loadAll, new Promise<boolean>((r) => window.setTimeout(() => r(false), maxMs))]);
}

/** Replace every tagged procedural prop under `root` with the real-asset
 *  variants that have loaded. Returns how many InstancedMeshes were replaced.
 *  Call only under the load screen (precompile). Safe to call twice:
 *  replaced meshes lose the tag. */
export function upgradeMapProps(root: THREE.Object3D): number {
  const targets: THREE.InstancedMesh[] = [];
  root.traverse((o) => {
    const im = o as THREE.InstancedMesh;
    if (im.isInstancedMesh && im.userData.propKind in FAMILY_OF) targets.push(im);
  });
  let n = 0;
  const m4 = new THREE.Matrix4();
  const pos = new THREE.Vector3(), quat = new THREE.Quaternion(), scl = new THREE.Vector3();
  for (const im of targets) {
    const kind = im.userData.propKind as PropKind;
    const fam = FAMILY_OF[kind];
    const list = variants[fam];
    if (!list.length || !im.parent) continue;
    const seat = SEAT[fam];
    const lift = typeof im.userData.propLift === "number" ? im.userData.propLift : 0.3;
    const count = im.count;
    for (let vi = 0; vi < list.length; vi++) {
      const per = Math.ceil((count - vi) / list.length);
      if (per <= 0) continue;
      const v = list[vi];
      // per-map material clone: disposeSubtree() calls material.dispose() on
      // everything it walks; textures stay shared. Wind materials keep their
      // onBeforeCompile through clone().
      const mat = v.mat.clone();
      mat.onBeforeCompile = v.mat.onBeforeCompile;
      if (kind === "darkRock") mat.color.copy(DARK_ROCK_TINT);
      const out = new THREE.InstancedMesh(v.geo, mat, per);
      const h = v.geo.boundingBox!.max.y; // normalised height (base at 0)
      for (let k = 0, j = vi; j < count; j += list.length, k++) {
        im.getMatrixAt(j, m4);
        m4.decompose(pos, quat, scl);
        // decor.ts placed the procedural centre at ground + sy*lift
        const ground = pos.y - scl.y * lift;
        const s = scl.x * seat.scale;
        pos.y = ground - h * s * seat.embed;
        scl.set(s, s, s);
        m4.compose(pos, quat, scl);
        out.setMatrixAt(k, m4);
      }
      out.instanceMatrix.needsUpdate = true;
      // real assets CAST the sun shadow (the procedural ones didn't): without
      // it a prop with real height reads as floating. A few dozen props in a
      // shadow pass that is time-capped at 60 Hz — cheap. Alpha-tested
      // foliage casts leaf-shaped shadows (three copies map + alphaTest into
      // its depth material).
      out.castShadow = true;
      out.receiveShadow = true;
      out.frustumCulled = im.frustumCulled;
      out.layers.mask = im.layers.mask;
      out.name = `${im.name || kind}:real${vi}`;
      out.computeBoundingSphere();
      im.parent.add(out);
    }
    im.parent.remove(im);
    im.geometry.dispose();
    (Array.isArray(im.material) ? im.material : [im.material]).forEach((m) => m.dispose());
    im.dispose();
    n++;
  }
  return n;
}
